import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { CustomerStage, CustomerValueFiguresDto, DateRange, FunnelStepsDto, ReportExportKey } from '@ecom/contracts';
import { STAGE_LABELS } from '@/components/customer-stage';
import { FILTERABLE_TABS, exportId, useReportsStore, type ExportFormat, type ReportTab } from '@/stores/reports.store';
import { ReportFilterBar } from '@/components/report-filters';
import { ReportViewsMenu } from '@/components/report-views';
import { CartsReport, CustomReport, GeographyReport, ProfitReport, ReturnsReport, ShippingReport, StaffReport, StockValueReport } from '@/pages/reports-v3';
import { useAuthStore } from '@/stores/auth.store';
import { can } from '@/lib/session';
import { useUiStore } from '@/i18n';
import { useT, useStatusLabel } from '@/i18n/use-t';
import { count, date, money, share } from '@/lib/format';
import { RANGE_PRESETS, isDay, monthLabel, ordered, presetOf, type RangePreset } from '@/lib/date-range';
import { labelOr } from '@/lib/labels';
import { statusTone } from '@/lib/order-status';
import { CoinsIcon, CartIcon, BoxIcon, UsersIcon } from '@/components/icons';
import { Page, Section, Segmented, Tabs } from '@/components/page';
import { TrendChart } from '@/components/trend-chart';
import { ReportTable } from '@/components/report-table';
import { useVersus } from '@/lib/report-versus';
import {
  Alert,
  Button,
  ButtonLink,
  EmptyState,
  Input,
  MetricCard,
  MetricSkeleton,
  Pill,
  Skeleton,
  cx,
} from '@/components/ui';

/**
 * Reports.
 *
 * One date range across every tab, chosen once at the top: a reports screen
 * where each report quietly has its own period is how two people end up
 * quoting different numbers for the same month.
 *
 * Every table exports to CSV from the server, over the whole range rather than
 * the rows on screen — the export is the escape hatch that keeps this screen
 * small, so anything fancier happens in a spreadsheet.
 *
 * The range sits in the header's actions and the reports are the header's
 * tabs, with the exports at the end of the tab row: they belong to whichever
 * report is open.
 */

/** `needs`: a permission beyond the page's own — the API refuses the report without it. */
type TabEntry = { id: ReportTab; key: string; exportKey: ReportExportKey | null; needs?: string };

/**
 * Eighteen reports in five groups (ADR-118 – ADR-122): what was sold and what
 * it earned, who buys, how orders go out and come back, what visitors do, and
 * the report someone builds themselves.
 */
const GROUPS: { id: string; key: string; tabs: TabEntry[] }[] = [
  {
    id: 'sales',
    key: 'reportGroup_sales',
    tabs: [
      { id: 'orders', key: 'reportOrders', exportKey: 'orders' },
      { id: 'profit', key: 'reportTab_profit', exportKey: 'profit' },
      { id: 'products', key: 'reportProducts', exportKey: 'top-products' },
      { id: 'finance', key: 'reportFinance', exportKey: 'finance' },
      { id: 'discounts', key: 'reportDiscounts', exportKey: 'discounts' },
      // Where orders came from (ADR-097).
      { id: 'sources', key: 'reportSources', exportKey: 'sources' },
    ],
  },
  {
    id: 'customers',
    key: 'reportGroup_customers',
    tabs: [
      // Top spenders by name and email: a customer look-up, not a figure.
      { id: 'customers', key: 'reportCustomers', exportKey: 'customers', needs: 'customers:read' },
      { id: 'retention', key: 'reportRetention', exportKey: 'retention' },
      // What customers are worth, by first source and stage (ADR-109).
      { id: 'value', key: 'reportCustomerValue', exportKey: 'customer-value' },
      { id: 'carts', key: 'reportTab_carts', exportKey: 'carts' },
    ],
  },
  {
    id: 'operations',
    key: 'reportGroup_operations',
    tabs: [
      { id: 'geography', key: 'reportTab_geography', exportKey: 'geography' },
      { id: 'shipping', key: 'reportTab_shipping', exportKey: 'shipping' },
      { id: 'returns', key: 'reportTab_returns', exportKey: 'returns' },
      { id: 'stock', key: 'reportTab_stock', exportKey: 'stock-value' },
      // About people: the team's own work.
      { id: 'staff', key: 'reportTab_staff', exportKey: 'staff', needs: 'staff:manage' },
    ],
  },
  {
    id: 'visitors',
    key: 'reportGroup_visitors',
    tabs: [
      // The store's own visits, step by step (ADR-108).
      { id: 'funnel', key: 'reportFunnel', exportKey: 'funnel' },
      { id: 'performance', key: 'reportPerformance', exportKey: 'product-performance' },
    ],
  },
  { id: 'custom', key: 'reportGroup_custom', tabs: [{ id: 'custom', key: 'reportTab_custom', exportKey: null }] },
];

const ALL_TABS = GROUPS.flatMap((group) => group.tabs);

export function ReportsPage() {
  const store = useReportsStore();
  const t = useT();
  const user = useAuthStore((state) => state.user);
  const [params, setParams] = useSearchParams();
  const allowed = (entry: TabEntry) => !entry.needs || can(user, entry.needs);
  // The open report is in the address, so a suggestion or a bookmark can open it (ADR-121).
  const requested = ALL_TABS.find((entry) => entry.id === params.get('tab') && allowed(entry));
  const tab: ReportTab = requested?.id ?? 'orders';
  const setTab = (next: ReportTab) => setParams((current) => ({ ...Object.fromEntries(current), tab: next }), { replace: true });
  const group = GROUPS.find((entry) => entry.tabs.some((item) => item.id === tab)) ?? GROUPS[0]!;
  const tabs = group.tabs.filter(allowed);

  // Product and customer names are resolved server-side, so a language change
  // is a refetch rather than a re-render.
  const locale = useUiStore((state) => state.locale);

  const { load, range, filters } = store;

  useEffect(() => {
    // A custom report runs when asked, not on every change of a column.
    if (tab !== 'custom') void load(tab);
  }, [load, tab, range.from, range.to, locale, filters]);

  const active = ALL_TABS.find((entry) => entry.id === tab) ?? ALL_TABS[0]!;

  // Each format minds its own busy state: an Excel file being built does not
  // stop the PDF beside it, and each button says which one is under way.
  const exportButton = (key: ReportExportKey, format: ExportFormat, label: string, variant: 'secondary' | 'ghost') => {
    const busy = store.exporting.includes(exportId(key, format));
    return (
      <Button variant={variant} size="sm" disabled={busy} aria-busy={busy} onClick={() => void store.exportReport(key, format)}>
        {busy ? t('loading') : label}
      </Button>
    );
  };

  const reportTabs = (
    <div className="flex flex-col gap-3">
      <Segmented
        label={t('reports')}
        value={group.id}
        onChange={(id: string) => setTab(GROUPS.find((entry) => entry.id === id)!.tabs.filter(allowed)[0]?.id ?? 'orders')}
        items={GROUPS.filter((entry) => entry.tabs.some(allowed)).map((entry) => ({ id: entry.id, label: t(entry.key as Parameters<typeof t>[0]) }))}
      />
      <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:gap-4">
        <Tabs
          className="min-w-0 xl:flex-1"
          label={t(group.key as Parameters<typeof t>[0])}
          value={tab}
          onChange={setTab}
          items={tabs.map((entry) => ({ id: entry.id, label: t(entry.key as Parameters<typeof t>[0]) }))}
        />

        {/*
          Three formats, one server-side table. CSV for machines, Excel for
          the accountant, PDF for whoever the report is being sent to.
        */}
        <div className="flex shrink-0 flex-wrap justify-end gap-1.5 xl:border-b xl:border-line xl:pb-1.5">
          <ReportViewsMenu tab={tab} onOpen={(view) => setTab(view.report as ReportTab)} />
          {active.exportKey ? (
            <>
              {exportButton(active.exportKey, 'xlsx', t('exportExcel'), 'secondary')}
              {exportButton(active.exportKey, 'pdf', t('exportPdf'), 'secondary')}
              {exportButton(active.exportKey, 'csv', t('exportCsv'), 'ghost')}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );

  return (
    <Page title={t('reports')} subtitle={t('reportsSubtitle')} actions={tab === 'stock' ? undefined : <RangePicker />} tabs={reportTabs}>
      {store.error ? <Alert>{store.error}</Alert> : null}
      {FILTERABLE_TABS.includes(tab) ? <ReportFilterBar /> : null}

      {tab === 'orders' ? <OrdersReport /> : null}
      {tab === 'funnel' ? <FunnelReport /> : null}
      {tab === 'products' ? <ProductsReport /> : null}
      {tab === 'performance' ? <PerformanceReport /> : null}
      {tab === 'customers' ? <CustomersReport /> : null}
      {tab === 'retention' ? <RetentionReport /> : null}
      {tab === 'value' ? <CustomerValueReport /> : null}
      {tab === 'finance' ? <FinanceReport /> : null}
      {tab === 'discounts' ? <DiscountsReport /> : null}
      {tab === 'sources' ? <SourcesReport /> : null}
      {tab === 'profit' ? <ProfitReport /> : null}
      {tab === 'stock' ? <StockValueReport /> : null}
      {tab === 'geography' ? <GeographyReport /> : null}
      {tab === 'shipping' ? <ShippingReport /> : null}
      {tab === 'returns' ? <ReturnsReport /> : null}
      {tab === 'carts' ? <CartsReport /> : null}
      {tab === 'staff' ? <StaffReport /> : null}
      {tab === 'custom' ? <CustomReport /> : null}
    </Page>
  );
}

function RangePicker() {
  const { range, setRange } = useReportsStore();
  const t = useT();

  const presets: { id: RangePreset; label: string }[] = [
    { id: 'last7', label: t('last7Days') },
    { id: 'last30', label: t('last30Days') },
    { id: 'month', label: t('thisMonth') },
  ];
  const current = presetOf(range);

  // A cleared or half-typed date changes nothing; a "from" typed past the "to"
  // means the same days in the other order.
  const change = (patch: Partial<DateRange>) => {
    const next = { ...range, ...patch };
    if (isDay(next.from) && isDay(next.to)) setRange(ordered(next));
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <label className="flex items-center gap-2 text-xs text-ink-muted">
        {t('from')}
        <Input
          type="date"
          dir="ltr"
          value={range.from}
          max={range.to}
          onChange={(event) => change({ from: event.target.value })}
          className="w-40"
        />
      </label>
      <label className="flex items-center gap-2 text-xs text-ink-muted">
        {t('to')}
        <Input
          type="date"
          dir="ltr"
          value={range.to}
          min={range.from}
          onChange={(event) => change({ to: event.target.value })}
          className="w-40"
        />
      </label>

      <div className="flex gap-1">
        {presets.map((preset) => (
          <Button
            key={preset.id}
            variant={current === preset.id ? 'secondary' : 'ghost'}
            size="sm"
            aria-pressed={current === preset.id}
            onClick={() => setRange(RANGE_PRESETS[preset.id]())}
          >
            {preset.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

function OrdersReport() {
  const { orders, sales, loading } = useReportsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const statusLabel = useStatusLabel();

  const versus = useVersus();

  if (loading && !orders) return <MetricSkeleton />;
  if (!orders) return null;

  return (
    <div className="flex flex-col gap-5">
      {/* Each figure against the period of the same length just before (ADR-108). */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label={t('revenue')}
          value={money(orders.totals.revenue, locale)}
          trend={versus(orders.totals.revenue, orders.previous?.revenue)}
          tone="mint"
          icon={<CoinsIcon />}
        />
        <MetricCard
          label={t('orderCount')}
          value={count(orders.totals.orderCount, locale)}
          trend={versus(orders.totals.orderCount, orders.previous?.orderCount)}
          tone="sky"
          icon={<CartIcon />}
        />
        <MetricCard
          label={t('averageOrderValue')}
          value={money(orders.totals.averageOrderValue, locale)}
          trend={versus(orders.totals.averageOrderValue, orders.previous?.averageOrderValue)}
          tone="violet"
          icon={<CoinsIcon />}
        />
        <MetricCard
          label={t('unitsSold')}
          value={count(orders.totals.unitsSold, locale)}
          trend={versus(orders.totals.unitsSold, orders.previous?.unitsSold)}
          tone="amber"
          icon={<BoxIcon />}
        />
      </div>

      <Section title={t('revenuePerDay')} subtitle={t('revenuePerDayHint')}>
        {/* The overview's chart: each day named, its revenue and orders on
            hover or by the arrow keys, and a table behind it for a screen reader. */}
        {sales.length === 0 ? (
          <p className="py-6 text-center text-sm text-ink-muted">{t('noDataForRange')}</p>
        ) : (
          <TrendChart
            points={sales}
            locale={locale}
            label={t('revenuePerDay')}
            ordersLabel={t('ordersWord')}
            emptyLabel={t('noDataForRange')}
            dimmed={loading}
          />
        )}
      </Section>

      <div className="grid gap-5 lg:grid-cols-2">
        <Section title={t('byStatus')} flush>
          <ReportTable
            headers={[t('status'), t('orderCount'), t('revenue')]}
            rows={orders.byStatus.map((row) => [
              <Pill key="s" tone={statusTone(row.status)}>
                {statusLabel(row.status)}
              </Pill>,
              count(row.orderCount, locale),
              money(row.revenue, locale),
            ])}
            empty={t('noDataForRange')}
          />
        </Section>

        <Section title={t('byPaymentMethod')} flush>
          <ReportTable
            headers={[t('paymentMethod'), t('orderCount'), t('revenue')]}
            rows={orders.byPaymentMethod.map((row) => [
              labelOr(t, `orderMethod${row.method}`, row.method),
              count(row.orderCount, locale),
              money(row.revenue, locale),
            ])}
            empty={t('noDataForRange')}
          />
        </Section>
      </div>
    </div>
  );
}

function ProductsReport() {
  const { products, loading } = useReportsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  if (loading && products.length === 0) return <Skeleton className="h-72" />;

  return (
    <Section title={t('reportProducts')} subtitle={t('reportProductsHint')} flush className="flex-1">
      <ReportTable
        headers={[t('sku'), t('product'), t('unitsSold'), t('revenue')]}
        rows={products.map((row) => [
          <span key="sku" data-numeric dir="ltr">
            {row.sku}
          </span>,
          row.productId ? (
            <Link key="name" to={`/products/${row.productId}`} className="hover:underline">
              {row.name}
            </Link>
          ) : (
            row.name
          ),
          count(row.unitsSold, locale),
          money(row.revenue, locale),
        ])}
        empty={t('noDataForRange')}
      />
    </Section>
  );
}

function CustomersReport() {
  const { customers, loading } = useReportsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  if (loading && !customers) return <MetricSkeleton />;
  if (!customers) return null;

  return (
    <div className="flex flex-col gap-5">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label={t('newAccounts')}
          value={count(customers.newCustomers, locale)}
          tone="sky"
          icon={<UsersIcon />}
        />
        <MetricCard
          label={t('firstTimeBuyers')}
          value={count(customers.firstTimeCustomers, locale)}
          tone="violet"
          icon={<UsersIcon />}
        />
        <MetricCard
          label={t('returningBuyers')}
          value={count(customers.returningCustomers, locale)}
          hint={t('returningBuyersHint')}
          tone="mint"
          icon={<UsersIcon />}
        />
        <MetricCard
          label={t('guestOrders')}
          value={count(customers.guestOrders, locale)}
          tone="amber"
          icon={<CartIcon />}
        />
      </div>

      <Section title={t('topSpenders')} flush>
        <ReportTable
          headers={[t('customer'), t('email'), t('orderCount'), t('totalSpent')]}
          rows={customers.topSpenders.map((row) => [
            // A buyer with an account opens it; a guest has none to open.
            row.id ? (
              <Link key="n" to={`/customers/${row.id}`} className="font-medium text-brand-700 hover:underline">
                {row.name || row.email}
              </Link>
            ) : (
              row.name || '—'
            ),
            <span key="e" dir="ltr">
              {row.email}
            </span>,
            count(row.orderCount, locale),
            money(row.totalSpent, locale),
          ])}
          empty={t('noDataForRange')}
        />
      </Section>
    </div>
  );
}

function FinanceReport() {
  const { finance, loading } = useReportsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  if (loading && !finance) return <MetricSkeleton />;
  if (!finance) return null;

  const lines: { label: string; value: number; strong?: boolean; muted?: boolean }[] = [
    { label: t('grossSales'), value: finance.grossSales },
    { label: t('campaignsGiven'), value: -finance.promotions, muted: true },
    { label: t('discountsGiven'), value: -finance.discounts, muted: true },
    { label: t('shippingCharged'), value: finance.shipping },
    { label: t('paymentFees'), value: finance.paymentFees },
    { label: t('netSales'), value: finance.netSales, strong: true },
    { label: t('vatCollected'), value: finance.vat, muted: true },
    { label: t('refunds'), value: -finance.refunds, muted: true },
    { label: t('netAfterRefunds'), value: finance.net, strong: true },
    // What the goods cost (ADR-118), and what is left once VAT, shipping, fees and refunds are out.
    ...(finance.costOfGoods > 0
      ? [
          { label: t('costOfGoods'), value: -finance.costOfGoods, muted: true },
          { label: t('grossProfit'), value: finance.net - finance.vat - finance.shipping - finance.paymentFees - finance.costOfGoods, strong: true },
        ]
      : []),
    // Of the cash-on-delivery sales, what reached the store and what is still
    // with the courier or waiting at the showroom (ADR-061).
    ...(finance.codCollected > 0 || finance.codOutstanding > 0
      ? [
          { label: t('codCollected'), value: finance.codCollected, muted: true },
          { label: t('codOutstanding'), value: finance.codOutstanding, muted: true },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-5">
      <Section title={t('financeSummary')} subtitle={t('financeSummaryHint')} flush>
        <div className="flex flex-col divide-y divide-line border-t border-line">
          {lines.map((line) => (
            <div
              key={line.label}
              className={cx(
                'flex items-baseline justify-between gap-4 px-5 py-3',
                line.strong && 'bg-surface-sunken',
              )}
            >
              <span className={cx('text-sm', line.strong ? 'font-semibold text-ink' : 'text-ink-muted')}>
                {line.label}
              </span>
              {/* Bidi-isolated: a negative amount inside an RTL row otherwise
                  renders its minus sign at the far end, where it reads as part
                  of the next thing rather than as a deduction. */}
              <bdi
                data-numeric
                className={cx('text-sm', line.strong ? 'font-semibold text-ink' : 'text-ink-muted')}
              >
                {money(line.value, locale)}
              </bdi>
            </div>
          ))}
        </div>
      </Section>

      <Section title={t('byMonth')} flush>
        <ReportTable
          headers={[t('month'), t('orderCount'), t('netSales'), t('vatCollected'), t('refunds')]}
          rows={finance.byMonth.map((row) => [
            monthLabel(row.month, locale),
            count(row.orderCount, locale),
            money(row.netSales, locale),
            money(row.vat, locale),
            money(row.refunds, locale),
          ])}
          empty={t('noDataForRange')}
        />
      </Section>
    </div>
  );
}

function DiscountsReport() {
  const { discounts, loading } = useReportsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  if (loading && discounts.length === 0) return <Skeleton className="h-72" />;

  return (
    <Section title={t('reportDiscounts')} subtitle={t('reportDiscountsHint')} flush className="flex-1">
      <ReportTable
        headers={[t('code'), t('type'), t('uses'), t('discountGiven'), t('revenue')]}
        rows={discounts.map((row) => [
          <span key="c" data-numeric dir="ltr">
            {row.code}
          </span>,
          labelOr(t, `type${row.type}`, row.type),
          count(row.uses, locale),
          money(row.discountGiven, locale),
          money(row.revenue, locale),
        ])}
        empty={t('noDataForRange')}
      />
    </Section>
  );
}

/**
 * Sales by source (ADR-097): what each channel sold and its share, then the
 * tagged campaigns that earned most — each with a link builder a click away.
 */
function SourcesReport() {
  const { sources, loading } = useReportsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  if (loading && !sources) return <Skeleton className="h-72" />;
  if (!sources) return null;

  return (
    <div className="flex flex-col gap-4">
      <Section
        title={t('reportSources')}
        subtitle={t('reportSourcesHint')}
        flush
        action={
          <ButtonLink to="/campaign-links" variant="secondary" size="sm">
            {t('campaignLinks')}
          </ButtonLink>
        }
      >
        <ReportTable
          headers={[t('sourceChannel'), t('orders'), t('revenue'), t('averageOrder'), t('sourceShare')]}
          rows={sources.byChannel.map((row) => [
            <span key="c" className="font-medium text-ink">
              {t(`channel_${row.channel}`)}
            </span>,
            count(row.orders, locale),
            money(row.revenue, locale),
            money(row.averageOrder, locale),
            share(row.revenue, sources.revenue, locale),
          ])}
          empty={t('noDataForRange')}
        />
      </Section>
      <Section title={t('sourceCampaigns')} subtitle={t('sourceCampaignsHint')} flush>
        <ReportTable
          headers={[t('sourceCampaign'), t('sourceChannel'), t('orders'), t('revenue')]}
          rows={sources.campaigns.map((row) => [
            <span key="c" className="block">
              <span className="block font-medium text-ink">{row.campaign ?? '—'}</span>
              <span dir="ltr" className="block text-start text-xs text-ink-muted">
                {[row.source, row.medium].filter(Boolean).join(' / ') || '—'}
              </span>
            </span>,
            t(`channel_${row.channel}`),
            count(row.orders, locale),
            money(row.revenue, locale),
          ])}
          empty={t('sourceCampaignsEmpty')}
        />
      </Section>
    </div>
  );
}

/** How a figure moved since the period of the same length before (ADR-108) — a card's second line. */

const FUNNEL_STEPS = [
  { key: 'visits', label: 'funnelVisits' },
  { key: 'viewedProduct', label: 'funnelViewed' },
  { key: 'addedToCart', label: 'funnelAdded' },
  { key: 'beganCheckout', label: 'funnelCheckout' },
  { key: 'ordered', label: 'funnelOrdered' },
] as const;

/**
 * The funnel (ADR-108): of the store's own visits, how many reached each
 * step — each a bar against all visits, its share of the step before, and its
 * change from the period before. Then the same by source and by device.
 */
function FunnelReport() {
  const { funnel, loading } = useReportsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const versus = useVersus();

  if (loading && !funnel) return <Skeleton className="h-72" />;
  if (!funnel) return null;
  if (!funnel.since) return <EmptyState title={t('reportFunnelNone')} icon={<UsersIcon />} />;

  const stepRows = (steps: FunnelStepsDto) => [
    count(steps.visits, locale),
    count(steps.viewedProduct, locale),
    count(steps.addedToCart, locale),
    count(steps.beganCheckout, locale),
    count(steps.ordered, locale),
    share(steps.ordered, steps.visits, locale),
  ];
  const headers = [t('funnelVisits'), t('funnelViewed'), t('funnelAdded'), t('funnelCheckout'), t('funnelOrdered'), t('funnelConversion')];

  return (
    <div className="flex flex-col gap-4">
      <Section title={t('reportFunnel')} subtitle={t('reportFunnelHint')}>
        <ol className="flex flex-col gap-4">
          {FUNNEL_STEPS.map((step, index) => {
            const value = funnel[step.key];
            const before = index > 0 ? funnel[FUNNEL_STEPS[index - 1]!.key] : null;
            const trend = versus(value, funnel.previous[step.key]);
            return (
              <li key={step.key} className="flex flex-col gap-1.5">
                <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                  <span className="text-sm font-medium text-ink">{t(step.label)}</span>
                  <span className="flex items-baseline gap-3 text-xs text-ink-muted">
                    {before !== null ? <span>{t('funnelOfPrevious', { share: share(value, before, locale) })}</span> : null}
                    {trend ? <span className={cx(trend.direction === 'up' ? 'text-success' : trend.direction === 'down' ? 'text-danger' : '')}>{trend.text}</span> : null}
                    <span data-numeric className="text-base font-semibold text-ink">
                      {count(value, locale)}
                    </span>
                  </span>
                </div>
                <div className="h-3 overflow-hidden rounded-full bg-surface-sunken" aria-hidden>
                  <div className="h-full rounded-full bg-brand-600" style={{ width: `${funnel.visits > 0 ? Math.max(1, (value / funnel.visits) * 100) : 0}%` }} />
                </div>
                {index > 0 ? <span className="text-xs text-ink-faint">{t('funnelOfVisits', { share: share(value, funnel.visits, locale) })}</span> : null}
              </li>
            );
          })}
        </ol>
        <p className="mt-4 text-xs text-ink-muted">{t('reportFunnelSince', { date: date(funnel.since, locale) })}</p>
      </Section>
      <Section title={t('funnelBySource')} flush>
        <ReportTable
          headers={[t('sourceChannel'), ...headers]}
          rows={funnel.bySource.map((row) => [
            <span key="c" className="font-medium text-ink">
              {t(`channel_${row.channel}`)}
            </span>,
            ...stepRows(row),
          ])}
          empty={t('noDataForRange')}
        />
      </Section>
      <Section title={t('funnelByDevice')} flush>
        <ReportTable
          headers={[t('funnelByDevice'), ...headers]}
          rows={funnel.byDevice.map((row) => [
            <span key="d" className="font-medium text-ink">
              {t(row.device === 'mobile' ? 'deviceMobile' : row.device === 'desktop' ? 'deviceDesktop' : 'deviceUnknown')}
            </span>,
            ...stepRows(row),
          ])}
          empty={t('noDataForRange')}
        />
      </Section>
    </div>
  );
}

/**
 * Product performance (ADR-108): sorted on the server — revenue, views,
 * conversion, or what sits on the shelf. The first hundred here; the export
 * carries every product.
 */
function PerformanceReport() {
  const { performance, performanceSort, setPerformanceSort, loading } = useReportsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  return (
    <Section
      title={t('reportPerformance')}
      subtitle={t('reportPerformanceHint')}
      flush
      action={
        <Segmented
          label={t('reportPerformance')}
          value={performanceSort}
          onChange={setPerformanceSort}
          items={[
            { id: 'revenue', label: t('performanceSortRevenue') },
            { id: 'views', label: t('performanceSortViews') },
            { id: 'conversion', label: t('performanceSortConversion') },
            { id: 'slow', label: t('performanceSortSlow') },
          ]}
        />
      }
    >
      {loading && !performance ? (
        <Skeleton className="m-5 h-72" />
      ) : performance ? (
        <>
          <ReportTable
            headers={[
              t('product'),
              t('performanceViews'),
              t('performanceAdds'),
              t('unitsSold'),
              t('revenue'),
              t('performanceConversion'),
              t('performanceStock'),
              t('performanceCover'),
            ]}
            rows={performance.rows.map((row) => [
              <span key="p" className="flex flex-wrap items-center gap-2">
                <Link to={`/products/${row.productId}`} className="font-medium text-ink hover:text-brand-700 hover:underline">
                  {row.name}
                </Link>
                {row.slow ? <Pill tone="warning">{t('performanceSlow')}</Pill> : null}
              </span>,
              count(row.views, locale),
              count(row.adds, locale),
              count(row.unitsSold, locale),
              money(row.revenue, locale),
              row.conversion === null ? t('dash') : count(Math.round(row.conversion * 1000) / 10, locale),
              count(row.stock, locale),
              row.coverDays === null ? t('dash') : count(row.coverDays, locale),
            ])}
            empty={t('noDataForRange')}
          />
          {performance.total > performance.rows.length ? (
            <p className="border-t border-line px-5 py-3 text-xs text-ink-muted">
              {t('performanceShown', { shown: count(performance.rows.length, locale), total: count(performance.total, locale) })}
            </p>
          ) : null}
        </>
      ) : null}
    </Section>
  );
}

/**
 * Retention (ADR-108): of each month's first-time buyers, the share who came
 * back within 1, 2, 3, 6 and 12 months — "too soon" where that much time has
 * not passed — and the period's repeat rate.
 */
function RetentionReport() {
  const { retention, loading } = useReportsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  if (loading && !retention) return <MetricSkeleton />;
  if (!retention) return null;
  const months = ['1', '2', '3', '6', '12'] as const;

  return (
    <div className="flex flex-col gap-5">
      <div className="grid gap-4 sm:grid-cols-3">
        <MetricCard label={t('retentionBuyers')} value={count(retention.buyers, locale)} tone="sky" icon={<UsersIcon />} />
        <MetricCard label={t('retentionRepeatBuyers')} value={count(retention.repeatBuyers, locale)} tone="mint" icon={<UsersIcon />} />
        <MetricCard
          label={t('retentionRepeatRate')}
          value={retention.repeatRate === null ? t('dash') : share(retention.repeatBuyers, retention.buyers, locale)}
          tone="violet"
          icon={<CartIcon />}
        />
      </div>
      <Section title={t('reportRetention')} subtitle={t('reportRetentionHint')} flush>
        <ReportTable
          headers={[t('retentionCohort'), t('retentionBuyers'), ...months.map((months) => t('retentionWithin', { months }))]}
          rows={retention.cohorts.map((cohort) => [
            <span key="m" className="font-medium text-ink">
              {monthLabel(cohort.month, locale)}
            </span>,
            count(cohort.buyers, locale),
            ...months.map((months) => {
              const value = cohort.returnedWithin[months];
              return value === null ? (
                <span key={months} className="text-ink-faint">
                  {t('retentionNotYet')}
                </span>
              ) : (
                share(Math.round(value * 1000), 1000, locale)
              );
            }),
          ])}
          empty={t('noDataForRange')}
        />
      </Section>
    </div>
  );
}

/**
 * What customers are worth (ADR-109): the period's new buyers and all they
 * have spent since, by where they first came from and the stage they are in
 * now — and, beside it, the stages at each of the last twelve month ends.
 */
function CustomerValueReport() {
  const { customerValue: report, loading } = useReportsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  if (loading && !report) return <MetricSkeleton />;
  if (!report) return null;
  const { totals } = report;
  const perCustomer = (figures: CustomerValueFiguresDto) =>
    figures.customers === 0 ? t('dash') : count(Math.round((figures.orders / figures.customers) * 10) / 10, locale);
  const row = (label: React.ReactNode, figures: CustomerValueFiguresDto) => [
    label,
    count(figures.customers, locale),
    money(figures.revenue, locale),
    money(figures.averageValue, locale),
    perCustomer(figures),
    figures.customers === 0 ? t('dash') : share(figures.repeatCustomers, figures.customers, locale),
  ];
  const headers = (first: string) => [first, t('customerValueCustomers'), t('customerValueLifetime'), t('customerValueAverage'), t('customerValueOrders'), t('customerValueRepeat')];

  return (
    <div className="flex flex-col gap-5">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard label={t('customerValueCustomers')} value={count(totals.customers, locale)} tone="sky" icon={<UsersIcon />} />
        <MetricCard label={t('customerValueAverage')} value={totals.customers === 0 ? t('dash') : money(totals.averageValue, locale)} tone="mint" icon={<CoinsIcon />} />
        <MetricCard label={t('customerValueOrders')} value={perCustomer(totals)} tone="violet" icon={<CartIcon />} />
        <MetricCard
          label={t('customerValueRepeat')}
          value={totals.customers === 0 ? t('dash') : share(totals.repeatCustomers, totals.customers, locale)}
          tone="amber"
          icon={<UsersIcon />}
        />
      </div>

      <Section title={t('customerValueBySource')} subtitle={t('customerValueBySourceHint')} flush>
        <ReportTable
          headers={headers(t('sourceChannel'))}
          rows={report.bySource.map((entry) =>
            row(
              <span key="c" className="font-medium text-ink">
                {t(`channel_${entry.channel}` as Parameters<typeof t>[0])}
              </span>,
              entry,
            ),
          )}
          empty={t('noDataForRange')}
        />
      </Section>

      <Section title={t('customerValueByStage')} subtitle={t('reportCustomerValueHint')} flush>
        <ReportTable
          headers={headers(t('customerStage'))}
          rows={report.byStage.map((entry) =>
            row(
              <span key="s" className="font-medium text-ink">
                {t(STAGE_LABELS[entry.stage])}
              </span>,
              entry,
            ),
          )}
          empty={t('noDataForRange')}
        />
      </Section>

      <Section title={t('customerValueStagesByMonth')} subtitle={t('customerValueStagesByMonthHint')}>
        <StagesByMonth months={report.stagesByMonth} />
      </Section>
    </div>
  );
}

/** Bottom to top: the healthy stages at the foot, the slipping ones on top — where a growing problem shows. */
const BUYER_STAGES = ['loyal', 'active', 'new', 'at_risk', 'lost'] as const;

const STAGE_COLOURS: Record<(typeof BUYER_STAGES)[number], string> = {
  loyal: 'var(--color-success)',
  active: 'var(--color-accent-mint-ink)',
  new: 'var(--color-info)',
  at_risk: 'var(--color-warning)',
  lost: 'var(--color-danger)',
};

/**
 * One column per month end, its buyers stacked by stage. Accounts that never
 * bought are counted beside the legend, not drawn: a store's sign-ups would
 * dwarf the buyers whose movement the chart is for.
 */
function StagesByMonth({ months }: { months: { month: string; counts: Record<CustomerStage, number> }[] }) {
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const buyers = (counts: Record<string, number>) => BUYER_STAGES.reduce((sum, stage) => sum + (counts[stage] ?? 0), 0);
  const highest = Math.max(1, ...months.map((month) => buyers(month.counts)));
  const latest = months[months.length - 1];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex h-48 items-end gap-1.5 sm:gap-2" role="img" aria-label={t('customerValueStagesByMonth')}>
        {months.map((month, index) => {
          const total = buyers(month.counts);
          const label = `${monthLabel(month.month, locale)}${index === months.length - 1 ? ` (${t('customerValueSoFar')})` : ''}`;
          return (
            <div
              key={month.month}
              className="flex h-full min-w-0 flex-1 flex-col justify-end"
              title={`${label}: ${BUYER_STAGES.map((stage) => `${t(STAGE_LABELS[stage])} ${count(month.counts[stage], locale)}`).join(' · ')}`}
            >
              <div className="flex flex-col-reverse overflow-hidden rounded-t-sm" style={{ height: `${(total / highest) * 100}%` }}>
                {BUYER_STAGES.map((stage) =>
                  month.counts[stage] > 0 ? (
                    <span key={stage} style={{ height: `${(month.counts[stage] / total) * 100}%`, backgroundColor: STAGE_COLOURS[stage] }} />
                  ) : null,
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex gap-1.5 sm:gap-2" aria-hidden>
        {months.map((month) => (
          <span key={month.month} className="min-w-0 flex-1 truncate text-center text-[11px] text-ink-muted">
            {monthLabel(month.month, locale).split(' ')[0]}
          </span>
        ))}
      </div>
      <ul className="flex flex-wrap gap-x-4 gap-y-1.5">
        {[...BUYER_STAGES].reverse().map((stage) => (
          <li key={stage} className="flex items-center gap-1.5 text-xs text-ink-muted">
            <span aria-hidden className="size-2.5 rounded-sm" style={{ backgroundColor: STAGE_COLOURS[stage] }} />
            {t(STAGE_LABELS[stage])}
            <span data-numeric className="text-ink">
              {count(latest?.counts[stage] ?? 0, locale)}
            </span>
          </li>
        ))}
        <li className="text-xs text-ink-muted">
          {t('stageProspect')}{' '}
          <span data-numeric className="text-ink">
            {count(latest?.counts.prospect ?? 0, locale)}
          </span>{' '}
          ({t('customerValueNotDrawn')})
        </li>
      </ul>
    </div>
  );
}

/** The one table shape every report uses — figures end-aligned, first column reads. */
